// src/components/approval/ApprovalLine.jsx
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef
} from "react";
import {
  listCustomApprovalLines,
  saveCustomApprovalLine,
  deleteCustomApprovalLine,
  saveApprovalLinesForDocument,
} from "../../api/approval";
import axios from "../../api/axios";
import ApprovalLineModal from "../../components/approval/ApprovalLineModal";
import ReferenceModal from "../../components/approval/ReferenceModal";

const ApprovalLine = forwardRef((props, ref) => {
  const [referenceUsers, setReferenceUsers] = useState([]);
  const [isApprovalLineModalOpen, setIsApprovalLineModalOpen] = useState(false);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
  const [approvalLines, setApprovalLines] = useState([]);
  const [referenceUserIds, setReferenceUserIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [lines, setLines] = useState([]);
  const projectId = localStorage.getItem("projectId");
  const rootEl = useRef(null);

  // ----- Users -----
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`/project/${projectId}/users-without-me/`);
        if (response.data?.results?.length > 0) {
          setUsers(response.data.results);
        } else if (Array.isArray(response.data) && response.data.length > 0) {
          setUsers(response.data);
        } else {
          console.log("DB에 사용자가 없음");
        }
      } catch (error) {
        console.error("사용자 목록 API 오류:", error);
      }
    };
    if (projectId) fetchUsers();
  }, [projectId]);

  // ----- Saved lines (템플릿) -----
  const refreshLines = async () => {
    try {
      const responseLines = await listCustomApprovalLines();
      const list = Array.isArray(responseLines?.data?.results)
        ? responseLines.data.results
        : [];
      setLines(list);
      if (!list.length) console.log("DB에 결재선 템플릿이 없음!!");
    } catch (error) {
      console.error("결재선 목록 API 오류:", error);
      setLines([]);
    }
  };

  useEffect(() => {
    refreshLines();
  }, []);

  // ----- 상신 저장(문서용) : 외부에서 호출 -----
  const saveApprovalLine = async (dnsOrId) => {
    if (approvalLines.length === 0) {
      alert("결재선을 추가해주세요.");
      return;
    }
    const hasEmptyApprover = approvalLines.some((line) => !line.approver_id);
    if (hasEmptyApprover) {
      alert("모든 결재자를 선택해주세요.");
      return;
    }

    const approvalLinesData = approvalLines.map((line, idx) => ({
      approver_id: line.approver_id,
      order: line.order ?? idx + 1,
      approval_type: line.approval_type ?? "sequential",
      approval_category: line.approval_category || "approval",
    }));

    const referenceData = referenceUserIds.map((userId) => ({
      user_id: userId,
    }));

    const payload = {
      doc_dns: dnsOrId,
      approval_lines: approvalLinesData,
      references: referenceData,
    };

    try {
      console.log("[ApprovalLine] POST /approval/lines/save/", payload);
      await saveApprovalLinesForDocument(payload);
      alert("결재가 상신되었습니다.");
      setApprovalLines([]);
    } catch (e) {
      console.error(
        "[ApprovalLine] saveApprovalLinesForDocument failed:",
        e?.response?.status,
        e?.response?.data
      );
      alert("결재 상신(라인 저장)에 실패했습니다.");
    }
  };

  // ✅ ref로 외부에서 접근 가능하게 등록
  useImperativeHandle(ref, () => ({
    save: saveApprovalLine,
    getLines: () => approvalLines,
    rootEl: rootEl.current,
    setLines: (lines) => setApprovalLines(lines),
  }));

  // ----- 이하 UI 영역 (기존 그대로 유지) -----
  const addApprover = (newApprover = null) => {
    if (newApprover) {
      setApprovalLines((prev) => [...prev, newApprover]);
      return;
    }
    const nextOrder =
      approvalLines.length > 0
        ? Math.max(...approvalLines.map((l) => l.order)) + 1
        : 1;
    setApprovalLines((prev) => [
      ...prev,
      {
        id: Date.now(),
        username: "",
        order: nextOrder,
        approval_type: "sequential",
        approval_category: "approval",
      },
    ]);
  };

  const removeApprover = (id) => {
    const updated = approvalLines
      .filter((l) => l.id !== id)
      .map((l, i) => ({ ...l, order: i + 1 }));
    setApprovalLines(updated);
  };

  const regroupAndNormalizeOrders = (linesArr) => {
    const groups = {};
    linesArr.forEach((l) => {
      if (!groups[l.order]) groups[l.order] = [];
      groups[l.order].push(l);
    });
    let cur = 1;
    const result = [];
    Object.keys(groups)
      .sort((a, b) => +a - +b)
      .forEach((order) => {
        const group = groups[order];
        group.forEach((l) => {
          l.order = cur;
          if (group.length > 1) l.approval_type = "parallel";
          result.push(l);
        });
        cur++;
      });
    return result;
  };

  const moveApproverByIndex = (fromIndex, toIndex) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= approvalLines.length ||
      toIndex >= approvalLines.length
    )
      return;
    const fromLine = approvalLines[fromIndex];
    const toLine = approvalLines[toIndex];
    const updated = [...approvalLines];
    updated[fromIndex] = { ...fromLine, order: toLine.order };
    setApprovalLines(regroupAndNormalizeOrders(updated));
  };

  const moveApproverByIndexForReorder = (fromIndex, toIndex) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= approvalLines.length ||
      toIndex >= approvalLines.length
    )
      return;
    const fromOrder = approvalLines[fromIndex]?.order;
    const toOrder = approvalLines[toIndex]?.order;
    if (!fromOrder || !toOrder) return;
    const swapped = approvalLines.map((l) => {
      if (l.order === fromOrder) return { ...l, order: toOrder };
      if (l.order === toOrder) return { ...l, order: fromOrder };
      return l;
    });
    setApprovalLines(regroupAndNormalizeOrders(swapped));
  };

  const handleApproverChange = (id, approver_id) => {
    setApprovalLines(
      approvalLines.map((l) => (l.id === id ? { ...l, approver_id } : l))
    );
  };

  const handleApprovalTypeChange = (id, approval_type) => {
    if (approval_type === "parallel") {
      const target = approvalLines.find((l) => l.id === id);
      if (!target) return;
      const updated = approvalLines.map((l) => {
        if (l.id === id) return { ...l, approval_type: "parallel" };
        if (l.order === target.order) return { ...l, approval_type: "parallel" };
        return l;
      });
      setApprovalLines(updated);
    } else {
      setApprovalLines(
        approvalLines.map((l) => (l.id === id ? { ...l, approval_type } : l))
      );
    }
  };

  const handleApprovalCategoryChange = (id, approval_category) => {
    setApprovalLines(
      approvalLines.map((l) => (l.id === id ? { ...l, approval_category } : l))
    );
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      const approvers = approvalLines.map((l) => ({
        approver_id: l.approver_id,
        order: l.order,
        approval_category: l.approval_category || "approval",
      }));
      await saveCustomApprovalLine({
        name: templateData.name,
        approvers,
      });
      alert("결재선이 저장되었습니다.");
      await refreshLines();
    } catch (error) {
      console.error(
        "템플릿 저장 실패:",
        error?.response?.status,
        error?.response?.data
      );
      alert("저장에 실패했습니다.");
    }
  };

  const onDeleteline = async (lineId) => {
    try {
      await deleteCustomApprovalLine(lineId);
    } catch (error) {
      console.error(
        "삭제 실패:",
        error?.response?.status,
        error?.response?.data
      );
      alert("삭제에 실패했습니다.");
      return;
    }
    alert("결재선이 삭제되었습니다.");
    await refreshLines();
  };

  // ✅ 여기부터는 기존 UI 전혀 수정 없음
  return (
    <div ref={rootEl}>
      <div className="rounded-lg border-gray-200 space-y-2">
        {/* 결재선 */}
        <section className="flex items-center flex-wrap gap-2 mb-3">
          <span className="text-sm font-medium text-gray-600 tracking-wide">
            결재선
          </span>
          <button
            type="button"
            onClick={() => setIsApprovalLineModalOpen(true)}
            className="px-2.5 py-1 text-xs font-medium border rounded-md text-gray-700 hover:bg-gray-200"
          >
            관리
          </button>

          {(() => {
            const orderGroups = {};
            approvalLines.forEach((line) => {
              if (!orderGroups[line.order]) orderGroups[line.order] = [];
              orderGroups[line.order].push(line);
            });

            return Object.keys(orderGroups)
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map((order) => {
                const group = orderGroups[order];
                const isParallel = group.length > 1;
                return (
                  <div
                    key={order}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full border-indigo-100"
                  >
                    <span className="text-xs font-medium text-indigo-600">
                      {order}
                    </span>
                    {group.map((line, idx) => (
                      <span key={line.id} className="text-sm text-gray-700">
                        {line.approver_name || "미선택"}
                        <span className="ml-1 text-xs text-gray-500">
                          {line.approval_category === "pre_approval"
                            ? "전결"
                            : "결재"}
                        </span>
                        {isParallel && idx < group.length - 1 && (
                          <span className="mx-1 text-gray-400">|</span>
                        )}
                      </span>
                    ))}
                  </div>
                );
              });
          })()}
        </section>

        {/* 참조인 */}
        <section className="flex items-center flex-wrap gap-2 mb-3">
          <span className="text-sm font-medium text-gray-600 tracking-wide">
            참조인
          </span>
          <button
            type="button"
            onClick={() => setIsReferenceModalOpen(true)}
            className="px-2.5 py-1 text-xs font-medium border rounded-md text-gray-700 hover:bg-gray-200"
          >
            관리
          </button>
          <div className="flex flex-wrap gap-2">
            {referenceUserIds.map((id) => {
              const user = users.find((u) => u.id === id);
              if (!user) return null;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-1 py-0.5 rounded bg-gray-100 border border-gray-200 text-sm text-gray-800"
                >
                  <span>
                    {user.last_name}
                    {user.first_name}
                  </span>
                  <span className="text-xs text-gray-500">{user.position}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setReferenceUserIds(referenceUserIds.filter((uid) => uid !== id))
                    }
                    className="ml-1 text-gray-400 hover:text-red-500"
                    title="삭제"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </section>

        {/* 모달 */}
        <ApprovalLineModal
          isOpen={isApprovalLineModalOpen}
          onClose={() => setIsApprovalLineModalOpen(false)}
          users={users}
          approvalLines={approvalLines}
          onAddApprover={addApprover}
          onRemoveApprover={removeApprover}
          onMoveApprover={moveApproverByIndex}
          onMoveApproverForReorder={moveApproverByIndexForReorder}
          onApproverChange={handleApproverChange}
          onApprovalTypeChange={handleApprovalTypeChange}
          onApprovalCategoryChange={handleApprovalCategoryChange}
          onLoadTemplate={(templateLines) => setApprovalLines(templateLines)}
          onSaveTemplate={handleSaveTemplate}
          onDeleteline={onDeleteline}
          lines={lines}
          setReferenceUsers={setReferenceUsers}
        />
        <ReferenceModal
          isOpen={isReferenceModalOpen}
          onClose={() => setIsReferenceModalOpen(false)}
          approvalLines={approvalLines}
          selectedIds={referenceUserIds}
          onChange={setReferenceUserIds}
          onSave={(ids) => setReferenceUserIds(ids)}
        />
      </div>
    </div>
  );
});

export default ApprovalLine;
